import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ReactComponent as AppleIcon } from '../../../../../assets/icons/apple-icon.svg';
import { ReactComponent as CardChip } from '../../../../../assets/icons/card-chip.svg';
import { ReactComponent as DeleteIcon } from '../../../../../assets/icons/delete-icon.svg';
import { ReactComponent as MasterCardLogo } from '../../../../../assets/icons/master-card-logo.svg';
import { appCurrentBranding, brandText } from '../../../../../config/branding';
import { useAppSelector } from '../../../../../redux/hooks';
import { fetchBasicInformations } from '../../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import CustomDropDown from '../../../../../shared-components/custom-drop-down/CustomDropDown';
import DeleteWarning from '../../../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SwitchButton from '../../../../../shared-components/switch-button-field/SwitchButton';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../../utils/api/end-consumer-v2';

interface AppleCardInterface {
  _id: string;
  isSelected: boolean;
  card: {
    holder: string;
    last4Digits: string;
    expiryMonth: string;
    expiryYear: string;
  };
}
interface CreditCardInterface {
  _id: string;
  isSelected: boolean;
  card: {
    holder: string;
    last4Digits: string;
    expiryMonth: string;
    expiryYear: string;
  };
}

interface PaymentMethodInterface {
  _id: string;
  appleCards: AppleCardInterface[];
  balance: string;
  creditCards: CreditCardInterface[];
  deposit: number;
  updatedAt: string;
  userCustomId: string;
  preferredPaymentMethod: string;
}

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element | null;
  rightCorner: string;
  notification: boolean;
}

/**
 * Payment Methods Component
 * @returns {any} - A React component that displays payment methods.
 */
function PaymentMethod() {
  const { t, i18n } = useTranslation('endConsumer');
  const isRTL = i18n.dir() === 'rtl';
  const dispatch = useDispatch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeApplePayIndex, setActiveApplePayIndex] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [cardData, setCardData] = useState<PaymentMethodInterface | null>(null);
  const { selectedEndConsumer, basicInformation } = useAppSelector((state) => state.endConsumerType);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: null,
    rightCorner: '',
    notification: false,
  });

  const creditCardCount = cardData?.creditCards.length ?? 0;
  const appleCardCount = cardData?.appleCards.length ?? 0;

  const CARD_WIDTH = 340;
  const CARD_GAP = 20;
  const STEP = CARD_WIDTH + CARD_GAP;

  const isWalletDefault = cardData?.preferredPaymentMethod === 'wallet';

  /**
   * Move to the next card in the carousel.
   * @returns {any} - void
   */
  const nextCard = () => {
    if (creditCardCount === 0) return;
    setActiveIndex((previous) => (previous + 1) % creditCardCount);
  };

  /**
   * Move to the previous card in the carousel.
   * @returns {any} - void
   */
  const previousCard = () => {
    if (creditCardCount === 0) return;
    setActiveIndex((previous) => (previous === 0 ? creditCardCount - 1 : previous - 1));
  };

  /**
   * Move to the next card in the carousel.
   * @returns {any} - void
   */
  const nextApplePayCard = () => {
    if (appleCardCount === 0) return;
    setActiveApplePayIndex((previous) => (previous + 1) % appleCardCount);
  };

  /**
   * Move to the previous card in the carousel.
   * @returns {any} - void
   */
  const previousApplePayCard = () => {
    if (appleCardCount === 0) return;
    setActiveApplePayIndex((previous) => (previous === 0 ? appleCardCount - 1 : previous - 1));
  };

  /**
   * Maps basic information from the Redux store to the payment method interface format.
   * @param {any} basicInfo - The basic information object from the store.
   * @returns {PaymentMethodInterface | null} The mapped payment method interface or null.
   */
  const mapBasicInformationToPaymentMethod = (basicInfo: any): PaymentMethodInterface | null => {
    if (!basicInfo) return null;

    const cardsList = basicInfo.cards || [];

    // Credit Cards (not Apple Pay and not deleted)
    const creditCards = cardsList
      .filter((c: any) => !c.isApplePayCard && (c.deletedAt === null || c.deletedAt === undefined))
      .map((c: any) => ({
        _id: c._id,
        isSelected: Boolean(c.isSelected),
        card: {
          holder: c.cardHolderName || c.meta?.card?.holder || '',
          last4Digits: c.last4 || c.meta?.card?.last4Digits || '',
          expiryMonth: c.expiryMonth || c.meta?.card?.expiryMonth || '',
          expiryYear: c.expiryYear || c.meta?.card?.expiryYear || '',
        },
      }))
      .sort((a: any, b: any) => (b.isSelected ? 1 : 0) - (a.isSelected ? 1 : 0));

    // Apple Cards (and not deleted)
    const appleCards = cardsList
      .filter((c: any) => c.isApplePayCard && (c.deletedAt === null || c.deletedAt === undefined))
      .map((c: any) => ({
        _id: c._id,
        isSelected: Boolean(c.isSelected),
        card: {
          holder: c.cardHolderName || c.meta?.card?.holder || '',
          last4Digits: c.last4 || c.meta?.card?.last4Digits || '',
          expiryMonth: c.expiryMonth || c.meta?.card?.expiryMonth || '',
          expiryYear: c.expiryYear || c.meta?.card?.expiryYear || '',
        },
      }))
      .sort((a: any, b: any) => (b.isSelected ? 1 : 0) - (a.isSelected ? 1 : 0));

    return {
      _id: basicInfo._id,
      appleCards,
      balance: basicInfo.wallet?.balance || '0',
      creditCards,
      deposit: Number(basicInfo.wallet?.deposit || 0),
      updatedAt: basicInfo.updatedAt || '',
      userCustomId: basicInfo.userCustomId || '',
      preferredPaymentMethod: basicInfo.preferredPaymentMethod || '',
    };
  };

  /**
   * Set a card as default.
   * @param {any} selectedCardId - The index of the card to set as default.
   * @param {any} type - The type
   */
  const handleSetDefault = async (
    selectedCardId: string,
    type: 'wallet' | 'applePay' | 'hyperpay',
  ) => {
    if (!selectedEndConsumer?.userCustomId) return;

    setIsLoading(true);
    const payload =
      type === 'wallet'
        ? { paymentMethod: 'wallet' }
        : { cardTokenizationObjectId: selectedCardId, paymentMethod: type };

    try {
      await endConsumerApi.setPrimaryPaymentMethod(selectedEndConsumer.userCustomId, payload);

      await dispatch(
        fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }) as any,
      );
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a card.
   * @param {any} cardId - The ID of the card to delete.
   * @returns {void} The result
   */
  const handleDelete = async (cardId: string) => {
    if (!selectedEndConsumer?.userCustomId) return;

    setIsLoading(true);
    try {
      await endConsumerApi.deletePaymentMethod(selectedEndConsumer.userCustomId, cardId);
      await dispatch(
        fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }) as any,
      );
      setIsOpen(false);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Close modal box function
   */
  const closeModal = () => {
    setIsOpen(false);
  };

  /**
   * Handle delete warning.
   * @param {any} cardId - The ID of the card to delete.
   */
  const handleDeleteWarning = (cardId: string) => {
    setIsOpen(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          title={t('deleteCard')}
          description={t('areYouSureYouWantToDeleteCard')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={closeModal}
          submitButtonLabel={t('delete')}
          submitButtonOnClick={() => handleDelete(cardId)}
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Helper function to calculate track transform CSS string
   * @param {number} index - The active index
   * @returns {string} The CSS transform string
   */
  const getTrackTransform = (index: number): string => {
    const offset = CARD_WIDTH / 2 + index * STEP;
    return isRTL ? `calc(-50% + ${offset}px)` : `calc(50% - ${offset}px)`;
  };

  /**
   * Render the cards carousel.
   * @returns {any} - JSX.Element
   */
  const renderCards = () => {
    return (
      <div className="cards-carousel">
        <div
          className="cards-track"
          style={{
            transform: `translateX(${getTrackTransform(activeIndex)})`,
          }}>
          {cardData?.creditCards.map((creditCard, creditCardIndex) => (
            <div key={creditCard._id || creditCardIndex} className="card-shell">
              <div className={`card-wrapper ${creditCardIndex === activeIndex ? 'active' : ''}`}>
                <div className="credit-card">
                  <p className="name">
                    {creditCard.card.holder} <MasterCardLogo />
                  </p>
                  <p className="number">•••• •••• •••• {creditCard.card.last4Digits}</p>
                  <div className="footer-wrap">
                    <div className="footer-container">
                      <p className="footer">
                        {t('valid')}{' '}
                        <span>
                          {creditCard.card.expiryMonth}/{creditCard.card.expiryYear}
                        </span>
                      </p>
                      <p className="footer">
                        {t('cvv')} <span>•••</span>
                      </p>
                    </div>
                    <CardChip />
                  </div>
                </div>
                <div className="card-controls">
                  <div className="default-toggle">
                    <SwitchButton
                      checked={creditCard.isSelected}
                      disabled={creditCard.isSelected}
                      change={() => handleSetDefault(creditCard._id, 'hyperpay')}
                    />
                    <span
                      style={{
                        color: creditCard.isSelected
                          ? 'var(--primary-color)'
                          : 'var(--black-color)',
                        fontSize: '12px',
                      }}>
                      {t('default')}
                    </span>
                  </div>
                  <DeleteIcon
                    onClick={() => handleDeleteWarning(creditCard._id)}
                    className="delete-icon"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="card-shell-bottom">
          <div className="carousel-indicators">
            <span className="current-index">{activeIndex + 1}</span>
            <span className="separator">/</span>
            <span className="total-count">{cardData?.creditCards.length}</span>
          </div>
          <button type="button" className="nav-btn left" onClick={previousCard}>
            ‹
          </button>
          <button type="button" className="nav-btn right" onClick={nextCard}>
            ›
          </button>
        </div>
      </div>
    );
  };

  /**
   * Render Apple Pay cards
   * @returns {any} - The rendered Apple Pay cards
   */
  const renderApplePay = () => {
    return (
      <div className="cards-carousel">
        <div
          className="cards-track"
          style={{
            transform: `translateX(${getTrackTransform(activeApplePayIndex)})`,
          }}>
          {cardData?.appleCards?.map((applePayCard, applePayCardIndex) => (
            <div key={applePayCard._id || applePayCardIndex} className="card-shell">
              <div
                className={`card-wrapper ${applePayCardIndex === activeApplePayIndex ? 'active' : ''}`}>
                <div className="credit-card apple-pay">
                  <p className="name">
                    {applePayCard.card.holder} <AppleIcon className="apple-icon" />
                  </p>
                  <p className="number">•••• •••• •••• {applePayCard.card.last4Digits}</p>
                  <div className="footer-wrap">
                    <div className="footer-container">
                      <p className="footer">
                        {t('valid')}{' '}
                        <span>
                          {applePayCard.card.expiryMonth}/{applePayCard.card.expiryYear}
                        </span>
                      </p>
                      <p className="footer">
                        {t('cvv')} <span>•••</span>
                      </p>
                    </div>
                    <CardChip />
                  </div>
                </div>
                <div className="card-controls">
                  <div className="default-toggle">
                    <SwitchButton
                      checked={applePayCard.isSelected}
                      disabled={applePayCard.isSelected}
                      change={() => handleSetDefault(applePayCard._id, 'applePay')}
                    />
                    <span
                      style={{
                        color: applePayCard.isSelected
                          ? 'var(--primary-color)'
                          : 'var(--black-color)',
                        fontSize: '12px',
                      }}>
                      {t('default')}
                    </span>
                  </div>
                  <DeleteIcon
                    onClick={() => handleDeleteWarning(applePayCard._id)}
                    className="delete-icon"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="card-shell-bottom">
          <div className="carousel-indicators">
            <span className="current-index">{activeApplePayIndex + 1}</span>
            <span className="separator">/</span>
            <span className="total-count">{cardData?.appleCards?.length}</span>
          </div>
          <button type="button" className="nav-btn left" onClick={previousApplePayCard}>
            ‹
          </button>
          <button type="button" className="nav-btn right" onClick={nextApplePayCard}>
            ›
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    if (!basicInformation || basicInformation.userCustomId !== selectedEndConsumer.userCustomId) {
      dispatch(fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }) as any);
    }
  }, [selectedEndConsumer?.userCustomId, basicInformation]);

  useEffect(() => {
    if (basicInformation) {
      const mappedData = mapBasicInformationToPaymentMethod(basicInformation);
      setCardData(mappedData);
      setActiveIndex(0);
      setActiveApplePayIndex(0);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [basicInformation]);

  return (
    <div className="payment-method-wrap" dir={i18n.dir()}>
      {isLoading ? (
        <div className="loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <ModalBox
            open={isOpen}
            title={modalView.title}
            content={modalView.content}
            closeModal={closeModal}
            header={modalView.header}
            rightCorner={modalView.rightCorner}
            notification={modalView.notification}
          />
          <div className="payment-method-container">
            <div className="payment-method-brand-card">
              <div className="card-design">
                <p className="card-title">
                  {appCurrentBranding.charAt(0).toUpperCase() + appCurrentBranding.slice(1)}{' '}
                  {t('wallet')}
                </p>
                <div className="card-balance">
                  <p className="balance-label">{t('yourBalance')}</p>
                  {cardData?.balance && (
                    <p className="balance-amount">
                      {ToFixedValue(cardData.balance)} {t(brandText.currency)}
                    </p>
                  )}
                </div>
              </div>
              <div className="default-toggle">
                <SwitchButton
                  checked={isWalletDefault}
                  disabled={isWalletDefault}
                  change={() => handleSetDefault(cardData?._id ?? '', 'wallet')}
                />
                <span
                  style={{
                    color: isWalletDefault ? 'var(--primary-color)' : 'var(--black-color)',
                    fontSize: '12px',
                  }}>
                  {t('default')}
                </span>
              </div>
            </div>
            <div className="payment-method-user-cards">
              <CustomDropDown
                icon={<MasterCardLogo />}
                arrowPosition="right"
                backgroundColor="plain"
                label={t('creditDebitCards')}
                count={creditCardCount}
                height={50}
                content={creditCardCount > 0 ? renderCards() : null}
                open={false}
              />
              <CustomDropDown
                icon={<AppleIcon />}
                label={t('applePay')}
                count={appleCardCount}
                backgroundColor="plain"
                arrowPosition="right"
                height={50}
                content={appleCardCount > 0 ? renderApplePay() : null}
                open={false}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PaymentMethod;
